import React from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  Wallet, DollarSign, Gift, Target, User, CheckCircle, TrendingUp, 
  PieChart as PieChartIcon
} from 'lucide-react';
import { COLORS } from '../../../constants/dashboard';
import ProgressRing from '../shared/ProgressRing';

const BudgetView = ({ 
  budget, 
  budgetUsed, 
  budgetPercentage, 
  campaigns, 
  setShowBudgetModal 
}) => {
  return (
    <div className="p-4" style={{ background: COLORS.background }}>
      {/* Top Stats Row */}
      <div className="grid grid-cols-6 gap-3 mb-4">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Wallet className="w-5 h-5" style={{ color: COLORS.primary }} />
            <span className="text-xs text-gray-500">{new Date().getFullYear()}</span>
          </div>
          <p className="text-lg font-bold" style={{ color: COLORS.primary }}>${(budget.annual_budget || 0).toLocaleString()}</p>
          <p className="text-xs text-gray-500">Annual Budget</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-orange-500" />
            <TrendingUp className="w-3 h-3 text-green-500" />
          </div>
          <p className="text-lg font-bold text-orange-600">${(budgetUsed || 0).toLocaleString()}</p>
          <p className="text-xs text-gray-500">Used This Year</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Gift className="w-5 h-5 text-green-500" />
            <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">{budget.annual_budget > 0 ? (100 - budgetPercentage).toFixed(0) : 100}%</span>
          </div>
          <p className="text-lg font-bold text-green-600">${((budget.annual_budget || 0) - (budgetUsed || 0)).toLocaleString()}</p>
          <p className="text-xs text-gray-500">Available</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Target className="w-5 h-5" style={{ color: COLORS.secondary }} />
            <span className="text-xs text-gray-500">Ratio</span>
          </div>
          <p className="text-lg font-bold" style={{ color: COLORS.secondary }}>{budget.match_ratio || 1}:1</p>
          <p className="text-xs text-gray-500">Match Rate</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <User className="w-5 h-5 text-purple-500" />
            <span className="text-xs text-gray-500">Cap</span>
          </div>
          <p className="text-lg font-bold text-purple-600">${(budget.match_cap_per_employee || 0).toLocaleString()}</p>
          <p className="text-xs text-gray-500">Per Employee</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            <span className="text-xs text-gray-500">Auto</span>
          </div>
          <p className="text-lg font-bold text-emerald-600">${(budget.auto_approve_limit || 100).toLocaleString()}</p>
          <p className="text-xs text-gray-500">Auto-Approve</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left - Budget Overview & Table */}
        <div className="col-span-8 space-y-4">
          {/* Budget Progress Card */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold" style={{ color: COLORS.accent }}>Budget Usage</h3>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>{(budgetPercentage || 35).toFixed(0)}% used</span>
              </div>
              <button 
                onClick={() => setShowBudgetModal(true)}
                className="px-3 py-1.5 text-sm rounded-lg font-medium"
                style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
              >
                Edit Settings
              </button>
            </div>
            <div className="mb-4">
              <div className="flex items-end gap-6">
                <div>
                  <p className="text-3xl font-bold" style={{ color: COLORS.primary }}>${(budget.annual_budget || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Total Annual Budget</p>
                </div>
                <div className="flex-1">
                  <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(budgetPercentage || 35, 100)}%`,
                        background: (budgetPercentage || 35) > 90 ? '#DC2626' : (budgetPercentage || 35) > 70 ? '#D97706' : COLORS.primary
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs">
                    <span className="text-orange-600 font-medium">${(budgetUsed || 0).toLocaleString()} spent</span>
                    <span className="text-green-600 font-medium">${((budget.annual_budget || 0) - (budgetUsed || 0)).toLocaleString()} remaining</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Monthly Spending Chart */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">Monthly Match Spending</span>
                <select className="text-xs border rounded-lg px-2 py-1">
                  <option>2025</option>
                  <option>2024</option>
                </select>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={[
                  { month: 'Jan', amount: 2800 },
                  { month: 'Feb', amount: 3200 },
                  { month: 'Mar', amount: 4100 },
                  { month: 'Apr', amount: 3800 },
                  { month: 'May', amount: 4500 },
                  { month: 'Jun', amount: 5200 },
                  { month: 'Jul', amount: 3900 },
                  { month: 'Aug', amount: 4800 },
                  { month: 'Sep', amount: 0 },
                  { month: 'Oct', amount: 0 },
                  { month: 'Nov', amount: 0 },
                  { month: 'Dec', amount: 0 },
                ]}>
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 10 }} tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Spent']} />
                  <Bar dataKey="amount" fill={COLORS.primary} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Campaign Budget Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
              <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Budget by Campaign</h3>
              <span className="text-xs text-gray-500">{campaigns.length} campaigns</span>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Campaign</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Raised</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Match</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Cost</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {(campaigns.length > 0 ? campaigns : [
                  { campaign_id: 1, title: 'Education Fund', charity_name: 'Local Schools Foundation', raised_amount: 8500, employer_match_enabled: true, employer_match_ratio: 1, status: 'active' },
                  { campaign_id: 2, title: 'Clean Water Initiative', charity_name: 'Water.org', raised_amount: 6200, employer_match_enabled: true, employer_match_ratio: 2, status: 'active' },
                  { campaign_id: 3, title: 'Healthcare Access', charity_name: 'Direct Relief', raised_amount: 4800, employer_match_enabled: true, employer_match_ratio: 1, status: 'active' },
                  { campaign_id: 4, title: 'Food Security', charity_name: 'Feeding America', raised_amount: 3500, employer_match_enabled: false, employer_match_ratio: 0, status: 'paused' },
                ]).map(c => {
                  const matchCost = (c.raised_amount || 0) * (c.employer_match_enabled ? (c.employer_match_ratio || 1) : 0);
                  return (
                    <tr key={c.campaign_id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{c.title}</p>
                        <p className="text-xs text-gray-500">{c.charity_name}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">${(c.raised_amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        {c.employer_match_enabled ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            {c.employer_match_ratio || 1}:1
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium" style={{ color: COLORS.primary }}>${matchCost.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {c.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right - Budget Stats & Settings */}
        <div className="col-span-4 space-y-4">
          {/* Budget Gauge */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Budget Health</h3>
              <span className="text-xs text-gray-500">Q{Math.ceil((new Date().getMonth() + 1) / 3)}</span>
            </div>
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <ProgressRing 
                  progress={100 - (budgetPercentage || 35)} 
                  size={120} 
                  strokeWidth={12} 
                  color={(budgetPercentage || 35) > 80 ? '#DC2626' : (budgetPercentage || 35) > 60 ? '#D97706' : COLORS.success} 
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold" style={{ color: COLORS.accent }}>{(100 - (budgetPercentage || 35)).toFixed(0)}%</span>
                  <span className="text-xs text-gray-500">Available</span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Monthly avg spend</span>
                <span className="font-medium">${((budgetUsed || 0) / (new Date().getMonth() + 1)).toFixed(0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Projected year-end</span>
                <span className="font-medium">${(((budgetUsed || 0) / (new Date().getMonth() + 1)) * 12).toFixed(0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Matching Rules */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Matching Rules</h3>
              <button 
                onClick={() => setShowBudgetModal(true)}
                className="text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors" 
                style={{ color: COLORS.primary }}
              >
                Edit
              </button>
            </div>
            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{ background: COLORS.light }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Match Ratio</span>
                  <span className="text-sm font-bold" style={{ color: COLORS.primary }}>{budget.match_ratio || 1}:1</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">Company matches ${budget.match_ratio || 1} for every $1</p>
              </div>
              <div className="p-3 rounded-lg" style={{ background: COLORS.light }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Employee Cap</span>
                  <span className="text-sm font-bold text-purple-600">${(budget.match_cap_per_employee || 1000).toLocaleString()}</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">Max match per employee/year</p>
              </div>
              <div className="p-3 rounded-lg" style={{ background: COLORS.light }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Auto-Approve</span>
                  <span className="text-sm font-bold text-emerald-600">${(budget.auto_approve_limit || 100).toLocaleString()}</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">Donations under this auto-approved</p>
              </div>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Spending by Category</h3>
              <PieChartIcon className="w-4 h-4 text-gray-400" />
            </div>
            <ResponsiveContainer width="100%" height={100}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Education', value: 35 },
                    { name: 'Health', value: 28 },
                    { name: 'Environment', value: 22 },
                    { name: 'Other', value: 15 },
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={25}
                  outerRadius={40}
                  paddingAngle={2}
                  dataKey="value"
                >
                  <Cell fill={COLORS.primary} />
                  <Cell fill="#EC4899" />
                  <Cell fill={COLORS.success} />
                  <Cell fill={COLORS.secondary} />
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, '']} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-1 text-xs mt-2">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLORS.primary }}></span>Education 35%</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-pink-500"></span>Health 28%</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLORS.success }}></span>Environment 22%</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLORS.secondary }}></span>Other 15%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetView;
